import { Badge, Button, Col, message, Popover, Row, Space, Table, TableColumnsType } from 'antd';
import Icon, { CopyOutlined } from '@ant-design/icons';
import { ReactComponent as ReceiptSvg } from 'assets/icons/receipt.svg';
import { useAntdTable, useMemoizedFn } from 'ahooks';
import { CoinsOrder, getCoinsOrderList, payWithOrderCode } from 'services/api/virtualAssets';
import { useMemo } from 'react';
import { css } from '@emotion/css';
import copyText from 'utils/copyText';
import Price from 'components/Price';
import StatusButton from 'components/StatusButton';
import styled from '@emotion/styled';
import CashierModal, { CashierModalMetaProps } from 'bizComponents/Cashier/CashierModal';
import useModal from 'hooks/useModal';
import { errorToasterWithServerMsg } from 'utils/errorToaster';
import dayjs from 'lib/dayjs';
import ContactContent from 'views/Main/components/AppHeader/components/ContactContent';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useTranslate } from '@tolgee/react';

const Operation = styled(StatusButton)`
  padding-left: 0;
`;

export interface TopupRecordsProps {}

interface RepayModalMetaProps extends CashierModalMetaProps {
  order: CoinsOrder;
}

const TopupRecords = (props: TopupRecordsProps) => {
  const language = useSystemLanguageValue();
  const { visible, meta, show, hide } = useModal<RepayModalMetaProps>(false);
  const { t } = useTranslate();

  const { tableProps, refresh } = useAntdTable(
    ({ current, pageSize }) =>
      getCoinsOrderList({
        pageNo: current,
        pageSize,
      }).then((res) => {
        const {
          data: { data },
        } = res;
        return {
          total: data.pageInfo.total,
          list: data.data,
        };
      }),
    {
      //   refreshDeps: [platformFilter, regionFilter, keywordFilter, refreshAgain],
      onError() {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );
  const doRePayOrder = useMemoizedFn(async (order: CoinsOrder) => {
    await payWithOrderCode({
      orderCode: order.orderCode,
      language,
    })
      .then((r) => {
        show({
          order,
          currency: order.currency,
          price: order.amount,
          config: r.data.data,
        });
      })
      .catch((e) => {
        errorToasterWithServerMsg(e);
      });
  });
  const columns: TableColumnsType<CoinsOrder> = useMemo(
    () => [
      {
        title: t('translation.订单编号'),
        dataIndex: 'orderCode',
        render: (orderCode) => {
          return (
            <Space>
              {orderCode}
              <CopyOutlined
                onClick={() => copyText(orderCode).then(() => message.success(t('trade.复制成功')))}
              />
            </Space>
          );
        },
      },
      {
        title: t('translation.订单金额'),
        dataIndex: 'amount',
        render: (_, record) => {
          return <Price type="normal" value={record.amount} currency={record.currency} />;
        },
      },
      {
        title: t('WhatsApp.成功充值点券'),
        dataIndex: 'virtualAssetsBalance',
      },
      {
        title: t('market.更新时间'),
        dataIndex: 'updateTimeStamp',
        render: (ts) => dayjs(Number(ts)).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: t('translation.订单状态'),
        dataIndex: 'orderStatus',
        render: (_, record) => {
          return {
            1: <Badge status="error" text={t('market.待支付')} />,
            2: <Badge status="success" text={t('market.已完成')} />,
            3: <Badge status="default" text={t('market.已取消')} />,
            4: <Badge status="default" text={t('market.已过期')} />,
          }[record.orderStatus];
        },
      },
      {
        title: t('操作'),
        key: 'ctrl',
        render: (_, record) => {
          const { orderStatus } = record;

          if (orderStatus !== 1) {
            return '-';
          }

          return (
            <Operation type="link" onClick={() => doRePayOrder(record)}>
              {t('translation.支付')}
            </Operation>
          );
        },
      },
    ],
    [doRePayOrder, t],
  );
  const onRefresh = useMemoizedFn(async () =>
    meta?.order ? doRePayOrder(meta?.order) : undefined,
  );
  const onPayed = useMemoizedFn(() => {
    hide();
    refresh();
  });
  return (
    <>
      <Row
        className={css`
          margin: 10px 0;
        `}
        justify="end"
      >
        <Col>
          <Popover trigger="click" content={<ContactContent />}>
            <Button>
              <Space align="center">
                <Icon component={ReceiptSvg} />
                {t('market.发票说明')}
              </Space>
            </Button>
          </Popover>
        </Col>
      </Row>
      <Table rowKey="id" columns={columns} {...tableProps} />
      <CashierModal
        onCancel={() => hide()}
        meta={meta}
        content={
          <div
            className={css`
              font-size: 22px;
            `}
          >
            {t('WhatsApp.x点券', { x: meta?.price || 0 })}
          </div>
        }
        onRefresh={onRefresh}
        onPayed={onPayed}
        visible={visible}
      />
    </>
  );
};
export default TopupRecords;
